import { Anchor, Box, Button, Center, Group, Image, Stack, TextInput } from "@mantine/core"
import { modals } from "@mantine/modals"
import { useTranslation } from "react-i18next"
import { useAPIResource, useAPIResources, useFormData } from "@/hooks"
import type { TContentSection, TUser } from "@/types"
import { contentAPI, setQueryCache } from "@/utils"
import { MarkdownEditor, ModalButton, ModalControls, RoleIcon } from "../controls"
import { RoleSelect } from "../forms"
import { AppTitle } from "./AppTitle"
import { icons } from "./icons"
import { Markdown } from "./Markdown"
import { PageLoader } from "./PageLoader"
import { RoleOnly, SuperOnly } from "./permissions"

export function Content(props: Partial<TContentSection>) {
  const { i18n } = useTranslation()
  const filters = { locale: i18n.language, ...props }
  const { data: { data = [] } = {}, isPending } = useAPIResources(contentAPI, {
    where: filters,
    ...setQueryCache(3600e3 * 24 * 7),
  })
  if (isPending) return <PageLoader />

  // Sort sections by order
  const sections = data.sort((a, b) => a.order - b.order)

  return (
    <Stack>
      {sections.map((section) => (
        <RoleOnly role={section.permission} key={section.id}>
          <Section key={section.id} section={section} />
        </RoleOnly>
      ))}
      <SuperOnly>
        <Center>
          <ModalButton title="Add Section" variant="subtle" leftSection={<icons.Add size={16} />}>
            <SectionEdit order={sections.length} {...filters} />
          </ModalButton>
        </Center>
      </SuperOnly>
    </Stack>
  )
}

function Section({ section }: { section: TContentSection }) {
  return (
    <Box pos="relative">
      <Stack>
        {section.picture && <Image src={section.picture} alt={section.title || ""} maw={800} />}
        {section.title && <AppTitle fz={24}>{section.title}</AppTitle>}
        {section.body && <Markdown>{section.body}</Markdown>}
        {section.link && (
          <Anchor href={section.link} target="_blank" rel="noopener noreferrer">
            <Button variant="navy" rightSection={<icons.External size={16} />}>
              {section.link_title || section.link}
            </Button>
          </Anchor>
        )}
        <SuperOnly>
          <Center>
            <ModalButton variant="subtle" title="Edit the section" leftSection={<RoleIcon role={section.permission} />}>
              <SectionEdit {...section} />
            </ModalButton>
          </Center>
        </SuperOnly>
      </Stack>
    </Box>
  )
}

function SectionEdit({ order = 0, ...data }: Partial<TContentSection>) {
  const api = useAPIResource(contentAPI, { id: data.id, invalidate: ["sections"] })

  const form = useFormData({
    data: api.data,
    initialValues: api.data ?? {
      ...data,
      order,
      type: data.type || "common",
      body: data.body || "",
      title: data.title || "",
      permission: "user" as TUser["role"],
    },
  })

  return (
    <>
      <TextInput placeholder="Title" {...form.getInputProps("title")} />
      <MarkdownEditor {...form.getInputProps("body")} />
      <TextInput placeholder="Picture URL" {...form.getInputProps("picture")} />
      <Group w="100%" grow>
        <TextInput type="number" placeholder="Order" maw={100} {...form.getInputProps("order")} />
        <RoleSelect label="" {...form.getInputProps("permission")} />
      </Group>
      <Group w="100%" grow>
        <TextInput placeholder="Link URL" {...form.getInputProps("link")} />
        <TextInput placeholder="Link Title" {...form.getInputProps("link_title")} />
      </Group>
      <ModalControls
        disabled={api.isUpdating || !form.isValid()}
        onSubmit={form.onSubmit(async (values) => {
          if (api.isUpdating) return

          if (data.id) {
            await api.update({ ...values, id: data.id })
          } else {
            await api.create({
              ...data,
              ...values,
            })
          }
          modals.closeAll()
        })}
      >
        {data.id && (
          <Button
            color="red"
            onClick={async () => {
              if (api.isUpdating || !data.id) return
              await api.remove(data.id)
              modals.closeAll()
            }}
          >
            Remove
          </Button>
        )}
      </ModalControls>
    </>
  )
}
